/**
 * @fileoverview FSM одного борта за тик симуляции.
 */

import { Snd } from "../audio/sound";
import { APRON, HOLD_SHORT, TAXI_Y, W } from "../constants/world";
import { cmdGoAround } from "../commands/ops";
import { moveToward } from "../plane/move";
import { orbitPos } from "../plane/orbit";
import { pathTo } from "../plane/path";
import { groundTaxiSpeed } from "../plane/taxiSpeed";
import type { GameHooks, GameState, Plane } from "../state/types";
import { angLerp, clamp, dist, lerp, rnd } from "../util/math";
import { crash } from "./crash";
import { killHazardsFor } from "./hazards";
import { addScore, breakStreak, bumpStreak, violation } from "./scoring";

/**
 * Обновить один борт.
 * @param phase — фаза смены 0…2 (влияет на терпение на орбите)
 */
export function updatePlane(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
  phase: number,
): void {
  p.blink += dt;
  p.strobe += dt;
  p.fade = Math.min(1, p.fade + dt * 1.4);
  p.warn = Math.max(0, p.warn - dt);

  switch (p.status) {
    case "approach":
      tickApproach(g, hooks, p, dt, phase);
      break;
    case "inbound":
      tickInbound(g, hooks, p, dt);
      break;
    case "landing":
      tickLanding(g, hooks, p, dt);
      break;
    case "vacating":
      tickVacating(g, hooks, p, dt);
      break;
    case "awaiting_gate":
      tickAwaitingGate(g, hooks, p, dt);
      break;
    case "taxi_gate":
      tickTaxiGate(g, hooks, p, dt);
      break;
    case "service":
      tickService(g, hooks, p, dt);
      break;
    case "ready":
      tickReady(g, hooks, p, dt);
      break;
    case "taxi_rwy":
      tickTaxiRwy(g, hooks, p, dt);
      break;
    case "hold_short":
      tickHoldShort(g, hooks, p, dt);
      break;
    case "takeoff":
      tickTakeoff(g, hooks, p, dt);
      break;
    case "goaround":
      tickGoAround(g, hooks, p, dt);
      break;
  }
}

function tickApproach(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
  phase: number,
): void {
  p.waited += dt;
  if (p.holdBonus > 0) p.holdBonus -= dt;
  p.pat -=
    p.patRate * dt * (p.holdBonus > 0 ? 0.45 : 1) * (1 + phase * 0.05);
  if (p.pat <= 0) {
    p.pat = 0;
    cmdGoAround(g, hooks, p);
    violation(g, hooks, `борт ${p.cs} исчерпал топливо ожидания`);
    return;
  }
  const idx = g.planes.filter((q) => q.status === "approach").indexOf(p);
  const o = orbitPos(p, p.holdBonus > 0 ? 0 : idx + 1);
  if (p.joining) {
    const d = dist(p.x, p.y, o.x, o.y);
    moveToward(p, o.x, o.y, p.T.spd * 1.25, dt);
    if (d < 40) p.joining = false;
  } else {
    p.orbit += p.orbitDir * dt * 0.3;
    const np = orbitPos(p, p.holdBonus > 0 ? 0 : idx + 1);
    const dx = np.x - p.x;
    const dy = np.y - p.y;
    p.ang = angLerp(p.ang, Math.atan2(dy, dx), Math.min(1, dt * 4));
    p.x = lerp(p.x, np.x, Math.min(1, dt * 3.2));
    p.y = lerp(p.y, np.y, Math.min(1, dt * 3.2));
  }
  p.alt = 1;
}

function tickInbound(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  p.waited += dt;
  const tgt = p.path[0];
  if (!tgt) return;
  const done = moveToward(p, tgt.x, tgt.y, p.T.spd, dt);
  p.landLeft = Math.max(0, (p.landLeft || 0) - p.T.spd * dt);
  p.alt = clamp((p.landLeft || 0) / Math.max(1, p.landTotal || 1), 0, 1);
  if (!done) return;
  p.path.shift();
  if (p.path.length === 0) {
    p.status = "landing";
    p.alt = 0;
    p.v = p.T.spd * 1.55;
    const r = g.runways[p.rwy!]!;
    const roll =
      p.T.roll * (1 + (r.snow / 100) * 0.95) * (1 + g.cross * 0.32);
    p.stopX = p.x + roll;
    p.decel = (p.v * p.v) / (2 * Math.max(60, roll));
    Snd.land();
    if (g.cross) {
      hooks.toast(
        `${p.cs}: боковой — пробег длиннее (~+${Math.round(g.cross * 32)}%)`,
        "wx",
      );
    }
    for (let i = 0; i < 16; i++) {
      g.sparks.push({
        x: p.x + rnd(-12, 12),
        y: p.y + rnd(-6, 6),
        vx: rnd(-70, 10),
        vy: rnd(-50, 50),
        t: rnd(0.3, 0.8),
        c: "#cfe6ff",
        s: rnd(1, 2.6),
      });
    }
  }
}

function tickLanding(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  const r = g.runways[p.rwy!]!;
  p.v = Math.max(28, p.v - (p.decel || 0) * dt);
  p.x += p.v * dt;
  p.ang = angLerp(p.ang, 0, dt * 5);
  if (g.cross) {
    p.y = r.y + Math.sin(g.elapsed * 3.2) * 6.5 * (p.v / 180);
  } else {
    p.y = lerp(p.y, r.y, dt * 4);
  }
  hooks.track?.(p.x - p.T.len * 0.2, p.y + 8, 3.2);
  hooks.track?.(p.x - p.T.len * 0.2, p.y - 8, 3.2);
  if (p.x > r.x2 + 8) {
    crash(
      g,
      hooks,
      p,
      `${p.cs} выкатился за пределы ${r.name}. Занос был слишком глубоким.`,
    );
    return;
  }
  if (p.v <= 30) {
    p.status = "vacating";
    p.gateAskT = g.elapsed;
    g.landings++;
    const clean = r.snow < 45;
    addScore(
      g,
      hooks,
      p.T.pts *
        (p.FK?.pts || 1) *
        (p.priority ? 1.7 : 1) *
        (clean ? 1 : 0.75) *
        (p.medevac ? 1.25 : 1),
      p,
      p.medevac
        ? "МЕДРЕЙС ПРИНЯТ"
        : p.priority
          ? "ПРИОРИТЕТ ПРИНЯТ"
          : "ПОСАДКА",
    );
    bumpStreak(g, hooks);
    g.waitSum += p.waited;
    g.waitCount++;
    p.waited = 0;
    Snd.ok();
    if (r.reservedFor === p.id) {
      r.reservedFor = null;
      r.reserveT = 0;
      r.reserveLabel = "";
      hooks.toast(
        `${r.name}: ${p.cs} принят — резерв снят, полоса снова общая`,
        "good",
      );
    }
    const ex = clamp(p.x + 70, r.x1 + 200, r.x2 - 40);
    p.path = [
      { x: ex, y: r.y },
      { x: ex, y: TAXI_Y },
    ];
    p.exitRwy = r;
  }
}

function finishVacating(g: GameState, p: Plane): void {
  if (p.exitRwy && p.exitRwy.plane === p) p.exitRwy.plane = null;
  p.rwy = null;
  killHazardsFor(g, p);
  if (p.pendingGate !== null && p.pendingGate !== undefined) {
    const gate = g.gates[p.pendingGate]!;
    p.gate = p.pendingGate;
    p.pendingGate = null;
    p.status = "taxi_gate";
    p.path = pathTo(p, { x: gate.x, y: gate.y }, true);
  } else {
    let spot = APRON.find(
      (a) =>
        !g.planes.some(
          (q) =>
            !q.dead &&
            q !== p &&
            q.apron === a &&
            dist(q.x || a.x, q.y || a.y, a.x, a.y) < 36,
        ),
    );
    if (!spot) {
      const base = APRON[p.id % APRON.length]!;
      spot = {
        x: base.x + ((p.id % 5) - 2) * 38,
        y: base.y + ((p.id % 3) - 1) * 32,
      };
    }
    p.apron = spot;
    p.path = pathTo(p, p.apron, true);
    p.status = "awaiting_gate";
  }
}

function tickVacating(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  const sp = groundTaxiSpeed(g, p);
  if (!p.path.length) {
    // Пустой путь: либо уже выбран гейт, либо уходим на перрон ждать назначения
    finishVacating(g, p);
    return;
  }
  const tgt = p.path[0]!;
  if (moveToward(p, tgt.x, tgt.y, sp, dt)) {
    p.path.shift();
    if (p.path.length === 0) {
      finishVacating(g, p);
    }
  }
  // Полоса свободна, как только борт ушел с оси (раньше 50px — слишком долго
  // держал «занято» и блокировал уборку/взлет при медленном съезде).
  if (
    p.exitRwy &&
    Math.abs(p.y - p.exitRwy.y) > 34 &&
    p.exitRwy.plane === p
  ) {
    p.exitRwy.plane = null;
  }
  hooks.track?.(p.x, p.y, 2.6);
}

function tickAwaitingGate(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  if (p.path.length) {
    const tgt = p.path[0];
    if (
      tgt &&
      moveToward(p, tgt.x, tgt.y, groundTaxiSpeed(g, p), dt)
    ) {
      p.path.shift();
    }
    hooks.track?.(p.x, p.y, 2.4);
  } else {
    p.v = 0;
    const gateFree = g.gates.some((gate) => !gate.plane);
    if (gateFree) p.waited += dt;
    p.pat = clamp(1 - p.waited / 38, 0, 1);
    if (p.waited > 38 && !p.delayed) {
      p.delayed = true;
      addScore(g, hooks, -50, p, "ЗАДЕРЖКА");
      breakStreak(g, hooks);
      hooks.toast(`${p.cs} простаивает без гейта`, "bad");
    }
    if (p.waited > 78 && !p.delayed2) {
      p.delayed2 = true;
      violation(g, hooks, `длительная задержка ${p.cs}`);
    }
  }
}

function tickTaxiGate(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  if (!p.path.length) {
    p.status = "service";
    p.svcTotal = p.T.svc;
    p.svcLeft = p.svcTotal;
    p.ang = -Math.PI / 2;
    p.apron = null;
    p.waited = 0;
    return;
  }
  const tgt = p.path[0]!;
  if (moveToward(p, tgt.x, tgt.y, groundTaxiSpeed(g, p), dt)) {
    p.path.shift();
    if (p.path.length === 0) {
      p.status = "service";
      p.svcTotal = p.T.svc;
      p.svcLeft = p.svcTotal;
      if (p.gate !== null) {
        p.x = g.gates[p.gate]!.x;
        p.y = g.gates[p.gate]!.y;
      }
      p.ang = -Math.PI / 2;
      p.apron = null;
      p.waited = 0;
    }
  }
  hooks.track?.(p.x, p.y, 2.4);
}

function tickService(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  p.v = 0;
  if (p.gate !== null) {
    const gate = g.gates[p.gate]!;
    p.x = lerp(p.x, gate.x, Math.min(1, dt * 6));
    p.y = lerp(p.y, gate.y, Math.min(1, dt * 6));
    p.ang = angLerp(p.ang, -Math.PI / 2, Math.min(1, dt * 5));
  }
  p.svcLeft -= dt;
  p.pat = 1 - clamp(p.svcLeft / Math.max(0.1, p.svcTotal), 0, 1);
  if (p.svcLeft <= 0) {
    p.status = "ready";
    p.waited = 0;
    p.pat = 1;
    p.patRate = 1 / 40;
    hooks.toast(
      `${p.cs} готов к вылету, гейт ${p.gate !== null ? g.gates[p.gate]!.name : "—"}`,
    );
    Snd.tone(700, 0.1, "triangle", 0.07);
  }
}

function tickReady(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  p.v = 0;
  p.waited += dt;
  p.pat -= p.patRate * dt;
  if (p.pat <= 0) {
    p.pat = 0;
    if (!p.delayed) {
      p.delayed = true;
      addScore(g, hooks, -50, p, "ЗАДЕРЖКА ВЫЛЕТА");
      breakStreak(g, hooks);
    }
    if (p.waited > 60 && !p.delayed2) {
      p.delayed2 = true;
      violation(g, hooks, `вылет ${p.cs} сорван`);
    }
  }
}

function tickTaxiRwy(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  if (!p.path.length) {
    p.status = "hold_short";
    p.waited = 0;
    p.pat = 1;
    p.patRate = 1 / 35;
    return;
  }
  const tgt = p.path[0]!;
  if (moveToward(p, tgt.x, tgt.y, groundTaxiSpeed(g, p), dt)) {
    p.path.shift();
    if (p.path.length === 0) {
      p.status = "hold_short";
      const hs = HOLD_SHORT[p.rwyWish!]!;
      p.x = hs.x;
      p.y = hs.y;
      p.waited = 0;
      p.pat = 1;
      p.patRate = 1 / 35;
    }
  }
  hooks.track?.(p.x, p.y, 2.4);
}

function tickHoldShort(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  p.v = 0;
  p.waited += dt;
  const r = g.runways[p.rwyWish!]!;
  const face = Math.atan2(r.y - p.y, 0.01);
  p.ang = angLerp(p.ang, face, Math.min(1, dt * 4));
  p.pat -= p.patRate * dt;
  if (p.pat <= 0) {
    p.pat = 0;
    if (!p.delayed3) {
      p.delayed3 = true;
      addScore(g, hooks, -50, p, "ПРОСТОЙ У ВПП");
      breakStreak(g, hooks);
    }
  }
}

function tickTakeoff(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  const r = g.runways[p.rwy!]!;
  if (p.path?.length) {
    const tgt = p.path[0]!;
    if (moveToward(p, tgt.x, tgt.y, p.T.taxi * 1.15, dt)) {
      p.path.shift();
      if (!p.path.length) {
        p.x = r.x1 + 90;
        p.y = r.y;
        p.ang = 0;
        p.v = 0;
        p.rollStarted = true;
      }
    }
    hooks.track?.(p.x, p.y, 2.4);
    return;
  }
  p.v += 96 * dt * (1 - r.snow / 260);
  p.x += p.v * dt;
  p.y = lerp(p.y, r.y, Math.min(1, dt * 6));
  p.ang = angLerp(
    p.ang,
    g.cross ? Math.sin(g.elapsed * 3) * 0.12 : 0,
    dt * 4,
  );
  if (p.v < 200) {
    hooks.track?.(p.x - 10, p.y + 8, 3);
    hooks.track?.(p.x - 10, p.y - 8, 3);
  }
  if (p.v >= 205) {
    p.alt = Math.min(1, p.alt + dt * 0.42);
    p.y -= dt * 10;
    if (r.plane === p && p.x > r.x2 - 260) r.plane = null;
  }
  if (p.x > r.x2 - 40 && p.v < 190) {
    crash(
      g,
      hooks,
      p,
      `${p.cs} не смог оторваться на заснеженной ${r.name}.`,
    );
    return;
  }
  if (p.x > W + 140) {
    if (r.plane === p) r.plane = null;
    p.dead = true;
    g.departures++;
    addScore(
      g,
      hooks,
      p.T.pts * (p.FK?.pts || 1) * (p.priority ? 1.6 : 1),
      p,
      "ВЗЛЕТ",
    );
    bumpStreak(g, hooks);
    g.waitSum += p.waited;
    g.waitCount++;
    killHazardsFor(g, p);
  }
}

function tickGoAround(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  dt: number,
): void {
  void hooks;
  p.gaT = (p.gaT || 0) + dt;
  const tgt = p.path[0];
  if (tgt) moveToward(p, tgt.x, tgt.y, p.T.spd * 1.2, dt);
  p.alt = 1;
  if ((p.gaT || 0) > 3.2) {
    p.status = "approach";
    p.joining = false;
    p.orbit = rnd(0, Math.PI * 2);
    const idx = g.planes.filter((q) => q.status === "approach").length;
    const o = orbitPos(p, idx + 1);
    p.x = o.x;
    p.y = o.y;
    p.path = [];
    p.gaT = 0;
  }
}
